import { paths } from "@/App";
import { getLatestTeamExternalId } from "@/lib/local-storage";
import { useCreateTeamMutation } from "@/lib/queries/team-queries";
import { useSelfQuery } from "@/lib/queries/user-queries";
import {
  INVITE_ID_STORAGE_KEY,
  TP_VIDEO_TOKEN_STORAGE_KEY,
} from "@/lib/session-storage";
import { CreateTeamDto, TeamDto } from "@repo/api/team";
import { SelfDto } from "@repo/api/user";
import { useEffect, useRef } from "react";
import { NavigateFunction, useNavigate } from "react-router";

type HandleSetupParams = {
  selfData: SelfDto;
  navigate: NavigateFunction;
  createTeam: (data: CreateTeamDto) => Promise<TeamDto>;
};

async function handleSetup({
  selfData,
  navigate,
  createTeam,
}: HandleSetupParams) {
  const inviteId = sessionStorage.getItem(INVITE_ID_STORAGE_KEY);

  if (inviteId) {
    return navigate(paths.invite(inviteId), { replace: true });
  }

  let teamExternalId = getLatestTeamExternalId(selfData.teamMemberships);

  if (!teamExternalId) {
    const teamName = selfData.firstName
      ? `${selfData.firstName}'s team`
      : "My team";

    const team = await createTeam({ name: teamName });
    teamExternalId = team.externalId;
  }

  const tpVideoToken = sessionStorage.getItem(TP_VIDEO_TOKEN_STORAGE_KEY);

  if (tpVideoToken) {
    return navigate(paths.thirdPartyVideoTeam(teamExternalId, tpVideoToken), {
      replace: true,
    });
  }

  return navigate(paths.home(teamExternalId), { replace: true });
}

export default function RootRedirect() {
  const navigate = useNavigate();
  const { data, isSuccess } = useSelfQuery();

  const { mutateAsync: createTeam } = useCreateTeamMutation({});

  const isSetupCalled = useRef(false);

  useEffect(() => {
    if (isSetupCalled.current || !isSuccess || !data) {
      return;
    }

    isSetupCalled.current = true;
    handleSetup({ selfData: data, navigate, createTeam });
  }, [isSuccess, data, navigate, createTeam]);

  return null;
}
